import { observer } from 'mobx-react-lite';
import * as React from 'react';
import {
  ELEMENT_TYPE_ARTICLE,
  ELEMENT_TYPE_NAVIGATION_SECTION,
  ELEMENT_TYPE_PICTURE,
  ELEMENT_TYPE_SECTION_GROUP
} from '../consts/element-types';
import TextWrapper from './wrappersComponents/text/TextWrapper';
import PictureWrapper from './wrappersComponents/picture/PictureWrapper';
import EditorStateMachine from '../model/EditorStateMachine';
import PictureEditModal from './wrappersComponents/picture/pictureEditModal/PictureEditModal';
import NavigationWrapper from './wrappersComponents/navigation/NavigationWrapper';
import SectionEditOverlay from '../components/SectionEditOverlay';
import SlidesModel from '../model/SlidesModel';
import LockedElementOverlay from '../components/LockedElementOverlay';

interface FCProps {
  iframeRef: React.MutableRefObject<HTMLIFrameElement>;
}

const ElementsWrapperRender: React.FC<FCProps> = observer(({ iframeRef }) => {
  // Handle Escape key to deselect section
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && EditorStateMachine.selectedSectionId !== null) {
        EditorStateMachine.deselectSection();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);



  const renderSectionEditOverlay = React.useCallback(() => {
    if (EditorStateMachine.selectedSectionId === null) return null;

    // Handle virtual section (footer-contact with ID -1)
    if (EditorStateMachine.selectedSectionId === -1) {
      // Use selectedSectionPosition directly for virtual sections
      if (!EditorStateMachine.selectedSectionPosition) return null;

      return (
        <SectionEditOverlay
          key="footer-contact-virtual"
          element={{
            elementType: ELEMENT_TYPE_SECTION_GROUP,
            elementId: -1,
            elementPosition: EditorStateMachine.selectedSectionPosition,
            isFirstFoundElement: false,
            isVisible: true,
            areaName: 'Footer Contact',
            templateId: null
          }}
          sectionId={-1}
          iframeElement={iframeRef.current}
        />
      );
    }

    const sectionData = EditorStateMachine.elements.get(
      EditorStateMachine.selectedSectionId
    );

    if (!sectionData || sectionData.elementType !== ELEMENT_TYPE_SECTION_GROUP) {
      return null;
    }

    return (
      <SectionEditOverlay
        key={EditorStateMachine.selectedSectionId}
        element={sectionData}
        sectionId={EditorStateMachine.selectedSectionId}
        iframeElement={iframeRef.current}
      />
    );
  }, [iframeRef]);

  let hoveredElement = null;
  if (EditorStateMachine.hoveredElementId !== null) {
    let hoveredElementData = EditorStateMachine.elements.get(
      EditorStateMachine.hoveredElementId
    );

    if (hoveredElementData) {
      //if (hoveredElementData.elementType === ELEMENT_TYPE_NAVIGATION_SECTION) {
      //  hoveredElement = (
      //    <NavigationWrapper
      //      key={hoveredElementData.elementId}
      //      navigationId={hoveredElementData.elementId}
      //      areaName={hoveredElementData.areaName}
      //      elementData={hoveredElementData}
      //      iframeElement={iframeRef.current}
      //    />
      //  );
      //}

      if (hoveredElementData.elementType === ELEMENT_TYPE_ARTICLE) {
        hoveredElement = (
          <TextWrapper
            key={hoveredElementData.elementId}
            articleId={hoveredElementData.elementId}
            areaName={hoveredElementData.areaName}
            elementData={hoveredElementData}
            iframeElement={iframeRef.current}
          />
        );
      }

      if (hoveredElementData.elementType === ELEMENT_TYPE_PICTURE) {
        hoveredElement = (
          <PictureWrapper
            key={hoveredElementData.elementId}
            pictureId={hoveredElementData.elementId}
            elementData={hoveredElementData}
            iframeElement={iframeRef.current}
          />
        );
      }
    }
  }

  let regeneratingDataElement = null;
  if (EditorStateMachine.elementRegeneratingDataId !== null) {
    let regeneratingDataElementData = EditorStateMachine.elements.get(
      EditorStateMachine.elementRegeneratingDataId
    );

    if (regeneratingDataElementData) {
      if (regeneratingDataElementData.elementType === ELEMENT_TYPE_ARTICLE) {
        regeneratingDataElement = (
          <TextWrapper
            key={regeneratingDataElementData.elementId}
            articleId={regeneratingDataElementData.elementId}
            areaName={regeneratingDataElementData.areaName}
            elementData={regeneratingDataElementData}
            iframeElement={iframeRef.current}
            showLoader={true}
          />
        );
      }
    }
  }

  // Render editable elements in section when in edit mode
  const renderEditableElements = React.useCallback(() => {
    if (!EditorStateMachine.isSectionInEditMode) return null;

    const elements = [];
    for (const editableElement of EditorStateMachine.editableSectionElements) {
      if (editableElement.elementPosition.width <= 0 || editableElement.elementPosition.height <= 0) {
        continue;
      }

      if (editableElement.elementType === ELEMENT_TYPE_ARTICLE) {
        elements.push(
          <TextWrapper
            key={editableElement.elementId}
            articleId={editableElement.elementId}
            areaName={editableElement.areaName}
            elementData={{
              ...editableElement,
              isVisible: true,
              isFirstFoundElement: false,
              templateId: editableElement.cmsTemplateId
            }}
            iframeElement={iframeRef.current}
          />
        );
      }

      if (editableElement.elementType === ELEMENT_TYPE_PICTURE) {
        elements.push(
          <PictureWrapper
            key={editableElement.elementId}
            pictureId={editableElement.elementId}
            elementData={{
              ...editableElement,
              isVisible: true,
              isFirstFoundElement: false,
              templateId: editableElement.cmsTemplateId
            }}
            iframeElement={iframeRef.current}
          />
        );
      }
    }

    return elements;
  }, [iframeRef]);

  // Render locked elements in section when in edit mode
  const renderLockedElements = React.useCallback(() => {
    if (!EditorStateMachine.isSectionInEditMode) return null;

    const elements = [];
    let index = 0;

    // Safety: if iframe is not available, render nothing but log for debugging
    const iframeEl = iframeRef.current;
    if (!iframeEl) {
      return null;
    }

    const iframeRect = iframeEl.getBoundingClientRect();


    for (const lockedElement of EditorStateMachine.lockedSectionElements) {
      if (lockedElement.elementPosition.width <= 0 || lockedElement.elementPosition.height <= 0) {
        index++;
        continue;
      }

      const uniqueKey = `${EditorStateMachine.selectedSectionId}-${lockedElement.elementType}-${index}`;
      elements.push(
        <LockedElementOverlay
          key={`locked-${EditorStateMachine.selectedSectionId}-${lockedElement.elementType}-${index}`}
          label={lockedElement.label}
          elementPosition={lockedElement.elementPosition}
          iframeElement={iframeEl}
          uniqueKey={`${EditorStateMachine.selectedSectionId}-${lockedElement.elementType}-${index}`}
          elementType={lockedElement.elementType}
        />
      );
      index++;
    }

    return elements;
  }, [iframeRef]);

  return (
    <div>
      {renderSectionEditOverlay()}
      {renderEditableElements()}
      {renderLockedElements()}
      {/* {hoveredElement} */}
      {regeneratingDataElement}
      <PictureEditModal />
    </div>
  );
});

export default ElementsWrapperRender;
